import { useTranslation } from "react-i18next";
import { useCallback, type ReactNode } from "react";
import { CalendarPlus, Lock, Pill } from "lucide-react";
import { CheckInIcon } from "@ascleo/icons/components/self-service/CheckIn";
import { Subtitle } from "../../components/typography/subtitle";
import { Title } from "../../components/typography/title";
import { useSizing } from "../../lib/use-sizing";
import { LocalEmergencyTile } from "./components/LocalEmergencyTile";
import { Tile } from "./components/Tile";

export type SelfServiceFlow =
  | "checkin"
  | "scheduler"
  | "prescription"
  | "emergency";

/**
 * Language in which practices author the free-text welcome message when the
 * shell does not say otherwise (the staff settings are German).
 */
const DEFAULT_WELCOME_MESSAGE_LANGUAGE = "de";

interface StartProps {
  onSelectFlow?: (flow: SelfServiceFlow) => void;
  /** Whether bridge-backed flows are connected. */
  isConnected?: boolean;
  /** Whether scheduler flow should be shown. When false, scheduler card is hidden. */
  schedulerAvailable?: boolean;
  /** Whether prescription flow should be shown. When false, prescription card is hidden. */
  prescriptionAvailable?: boolean;
  /** Custom welcome message from practice settings. Falls back to i18n default. */
  welcomeMessage?: string;
  /**
   * Language the custom welcome message is written in (the kiosk's configured
   * default language). Defaults to German. See {@link resolveWelcomeHeadline}.
   * @since 1.3.0
   */
  welcomeMessageLanguage?: string;
}

/**
 * Rule for the hub greeting (KO-30): the practice's welcome message is a
 * single free text in one language. It is shown only while the session
 * language matches the language it was written in; in every other session
 * language the translated default greeting is shown instead, so a patient
 * never reads a greeting in a language they did not choose.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * resolveWelcomeHeadline({ welcomeMessage: "Willkommen in der Praxis Muster", welcomeMessageLanguage: "de", sessionLanguage: "tr", fallback: "Hoş geldiniz" });
 * // "Hoş geldiniz"
 * ```
 */
export function resolveWelcomeHeadline({
  welcomeMessage,
  welcomeMessageLanguage,
  sessionLanguage,
  fallback,
}: {
  readonly welcomeMessage: string | undefined;
  readonly welcomeMessageLanguage: string;
  readonly sessionLanguage: string;
  readonly fallback: string;
}): string {
  const message = welcomeMessage?.trim();
  if (!message) return fallback;
  return baseLanguage(sessionLanguage) === baseLanguage(welcomeMessageLanguage)
    ? message
    : fallback;
}

function baseLanguage(language: string): string {
  return (language.split("-")[0] ?? language).toLowerCase();
}

interface StartHubProps extends StartProps {
  readonly children: ReactNode;
}

interface HubTile {
  readonly flow: Exclude<SelfServiceFlow, "emergency" | "checkin">;
  readonly testId: string;
  readonly tone: "sky-subtle" | "orange-subtle";
  readonly icon: ReactNode;
  readonly nameKey: string;
  readonly descriptionKey: string;
}

/**
 * Self-service start screen — flow selection as a colored-tile hub.
 *
 * Check-in is the large `blue-ink` hero; scheduler (`sky-subtle`) and
 * prescription (`orange-subtle`) stack beside it. The secondary tiles are
 * composed from the availability props, so an unavailable flow has no tile at
 * all (FC-11). Tiles are content-sized and the hub is centred in the content
 * area instead of stretching to the viewport (KO-15); below 48rem of width
 * the tiles stack (FC-18). One short instruction per tile, no internal
 * eyebrows (KO-31). Offline, Bridge-backed tiles render as solid disabled
 * tiles with a lock hint (KO-34).
 */
function StartHub({
  onSelectFlow,
  isConnected = true,
  schedulerAvailable = true,
  prescriptionAvailable = false,
  welcomeMessage,
  welcomeMessageLanguage = DEFAULT_WELCOME_MESSAGE_LANGUAGE,
  children,
}: StartHubProps) {
  const { t, i18n } = useTranslation();
  const sizing = useSizing();
  const bridgeFlowsDisabled = !isConnected;

  const displayWelcome = resolveWelcomeHeadline({
    welcomeMessage,
    welcomeMessageLanguage,
    sessionLanguage: i18n?.resolvedLanguage ?? i18n?.language ?? DEFAULT_WELCOME_MESSAGE_LANGUAGE,
    fallback: t("self-service.start.welcome"),
  });

  const offlineDesc = (
    <span className="flex items-center gap-2">
      <Lock aria-hidden className="size-5" />
      {t("self-service.offline.flow-disabled")}
    </span>
  );

  const secondaryTiles: HubTile[] = [];
  if (schedulerAvailable) {
    secondaryTiles.push({
      flow: "scheduler",
      testId: "flow-scheduler",
      tone: "sky-subtle",
      icon: <CalendarPlus />,
      nameKey: "self-service.start.scheduler-cta",
      descriptionKey: "self-service.start.scheduler-description",
    });
  }
  if (prescriptionAvailable) {
    secondaryTiles.push({
      flow: "prescription",
      testId: "flow-prescription",
      tone: "orange-subtle",
      icon: <Pill />,
      nameKey: "self-service.start.prescription-cta",
      descriptionKey: "self-service.start.prescription-description",
    });
  }

  return (
    <div className="@container/hub mx-auto flex min-h-full w-full max-w-7xl min-w-0 flex-col justify-center gap-6 px-2 py-6">
      <header className="text-center">
        <Title
          size={sizing.title}
          className="font-heading font-normal tracking-[-0.025em] text-balance"
        >
          {displayWelcome}
        </Title>
        <Subtitle size={sizing.subtitle} className="mt-2 text-muted-foreground">
          {t("self-service.start.subtitle")}
        </Subtitle>
      </header>

      <div
        className={
          secondaryTiles.length > 0
            ? "grid min-w-0 grid-cols-1 gap-4 @3xl/hub:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]"
            : "mx-auto grid w-full max-w-3xl min-w-0 grid-cols-1 gap-4"
        }
      >
        <Tile
          tone="blue-ink"
          size="hero"
          className="min-h-56"
          data-testid="flow-checkin"
          disabled={bridgeFlowsDisabled}
          name={t("self-service.start.check-in-cta")}
          desc={
            bridgeFlowsDisabled
              ? offlineDesc
              : t("self-service.start.check-in-description")
          }
          icon={<CheckInIcon />}
          iconSize="large"
          onClick={() => onSelectFlow?.("checkin")}
        />

        {secondaryTiles.length > 0 ? (
          <div className="grid min-w-0 grid-cols-1 content-start gap-4">
            {secondaryTiles.map((tile) => (
              <Tile
                key={tile.flow}
                tone={tile.tone}
                className="min-h-40"
                data-testid={tile.testId}
                disabled={bridgeFlowsDisabled}
                name={t(tile.nameKey)}
                desc={bridgeFlowsDisabled ? offlineDesc : t(tile.descriptionKey)}
                icon={tile.icon}
                iconSize="large"
                onClick={() => onSelectFlow?.(tile.flow)}
              />
            ))}
          </div>
        ) : null}

        {children}
      </div>
    </div>
  );
}

/**
 * Self-service hub without a local emergency capability.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <Start />
 * ```
 */
export function Start(props: StartProps) {
  return <StartHub {...props}>{null}</StartHub>;
}

/**
 * Self-service hub composed with the practice-authorized local alert. The
 * alert band sits directly below the tiles, so it is visible at every
 * viewport whenever the policy permits it (KO-26).
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <StartWithLocalEmergency />
 * ```
 */
export function StartWithLocalEmergency(props: StartProps) {
  const { onSelectFlow } = props;
  const selectEmergency = useCallback(
    function selectEmergencyFlow() {
      onSelectFlow?.("emergency");
    },
    [onSelectFlow],
  );

  return (
    <StartHub {...props}>
      <LocalEmergencyTile onSelect={selectEmergency} />
    </StartHub>
  );
}
